import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { Printer, CircleNotch, PaperPlaneTilt } from "@phosphor-icons/react";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import { toast } from "@/components/ui/sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import api from "@/lib/api";

const CAT_COLORS = ["#1d4ed8", "#b45309", "#047857", "#be123c", "#7c3aed"];
const SYS_COLORS = ["#0f766e", "#7c3aed", "#c2410c", "#0369a1"];
const cap = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : s);

function ProblemFigure({ areas, tensions, catColor }) {
  const N = areas.length, R = 38;
  const pos = {};
  areas.forEach((a, i) => { const ang = -Math.PI / 2 + (2 * Math.PI * i) / N; pos[a.id] = { x: 50 + R * Math.cos(ang), y: 50 + R * Math.sin(ang) }; });
  return (
    <div className="relative mx-auto aspect-square w-full max-w-[440px]">
      <svg viewBox="0 0 100 100" className="absolute inset-0 h-full w-full" style={{ overflow: "visible" }}>
        <circle cx="50" cy="50" r={R} fill="none" stroke="#d4d4d8" strokeWidth="0.3" />
        {tensions.map((t) => { const a = pos[t.area_a], b = pos[t.area_b]; if (!a || !b) return null; return <line key={t.id} x1={a.x} y1={a.y} x2={b.x} y2={b.y} stroke="#9ca3af" strokeWidth="0.5" />; })}
      </svg>
      <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 rounded-full border border-zinc-300 bg-white px-3 py-2 text-center">
        <div className="text-[9px] uppercase tracking-wide text-zinc-500">Problem space</div>
        <div className="text-[11px] font-medium">{N} categories</div>
      </div>
      {areas.map((a) => { const p = pos[a.id]; return (
        <div key={a.id} className="absolute w-[130px] -translate-x-1/2 -translate-y-1/2" style={{ left: `${p.x}%`, top: `${p.y}%` }}>
          <div className="border-l-[3px] bg-white p-1.5 text-left shadow-sm" style={{ borderLeftColor: catColor[a.id] }}>
            <div className="text-[10px] font-semibold leading-tight">{cap(a.name)}</div>
            <div className="text-[8px] text-zinc-500">{a.needs.length} problems</div>
          </div>
        </div>
      ); })}
    </div>
  );
}

function SolutionsFigure({ areas, solutions, catColor, sysColor }) {
  const Ro = 45, Ri = 22;
  const spos = {}; solutions.forEach((s, i) => { const ang = -Math.PI / 2 + (2 * Math.PI * i) / solutions.length; spos[s.id] = { x: 50 + Ro * Math.cos(ang), y: 50 + Ro * Math.sin(ang) }; });
  const cpos = {}; areas.forEach((a, i) => { const ang = -Math.PI / 2 + (2 * Math.PI * i) / areas.length; cpos[a.id] = { x: 50 + Ri * Math.cos(ang), y: 50 + Ri * Math.sin(ang) }; });
  const fitOf = (sys, nid) => (sys.need_fit || []).find((f) => f.need_id === nid);
  const addr = (sys, a) => (a.needs || []).some((n) => { const f = fitOf(sys, n.id); return f && f.fit !== "strains"; });
  return (
    <div className="relative mx-auto aspect-square w-full max-w-[460px]">
      <svg viewBox="0 0 100 100" className="absolute inset-0 h-full w-full" style={{ overflow: "visible" }}>
        <circle cx="50" cy="50" r={Ri} fill="none" stroke="#d4d4d8" strokeWidth="0.3" />
        {solutions.map((s) => areas.filter((a) => addr(s, a)).map((a) => <line key={s.id + a.id} x1={spos[s.id].x} y1={spos[s.id].y} x2={cpos[a.id].x} y2={cpos[a.id].y} stroke={sysColor[s.id]} strokeWidth="0.4" opacity="0.4" />))}
      </svg>
      {areas.map((a) => { const p = cpos[a.id]; return (
        <div key={a.id} className="absolute w-[104px] -translate-x-1/2 -translate-y-1/2" style={{ left: `${p.x}%`, top: `${p.y}%` }}>
          <div className="border-l-[3px] bg-white px-1.5 py-1 text-left shadow-sm" style={{ borderLeftColor: catColor[a.id] }}><div className="text-[9px] font-semibold leading-tight">{cap(a.name)}</div></div>
        </div>
      ); })}
      {solutions.map((s, i) => { const p = spos[s.id]; return (
        <div key={s.id} className="absolute w-[130px] -translate-x-1/2 -translate-y-1/2" style={{ left: `${p.x}%`, top: `${p.y}%` }}>
          <div className="border-l-[3px] bg-white p-1.5 text-left shadow-sm" style={{ borderLeftColor: sysColor[s.id] }}>
            <div className="font-mono text-[8px]" style={{ color: sysColor[s.id] }}>SYSTEM {String.fromCharCode(65 + i)}</div>
            <div className="text-[10px] font-semibold leading-tight">{s.title}</div>
          </div>
        </div>
      ); })}
    </div>
  );
}

const roleOfN = (n) => (n.kind === "intervention" || n.kind === "solution") ? "strategy" : (n.kind === "problem" && n.parent) ? "sub" : "problem";
const EC = { improve: "#16a34a", enable: "#2563eb", change: "#8A3FFC", worse: "#dc2626", better: "#16a34a", enables: "#2563eb", other: "#94a3b8" };

function deriveSystem(map) {
  const nodes = (map.nodes || []).filter((n) => roleOfN(n) !== "sub");
  const edges = (map.edges || []).filter((e) => e.group !== "detail");
  const byId = Object.fromEntries(nodes.map((n) => [n.id, n]));
  const problems = nodes.filter((n) => roleOfN(n) === "problem");
  const strategies = nodes.filter((n) => roleOfN(n) === "strategy");
  const sset = new Set(strategies.map((s) => s.id));
  const improves = {}, incoming = {};
  strategies.forEach((s) => { improves[s.id] = 0; incoming[s.id] = 0; });
  edges.forEach((e) => {
    if (sset.has(e.source) && byId[e.target] && roleOfN(byId[e.target]) === "problem" && e.group !== "changes_link") improves[e.source]++;
    if (sset.has(e.source) && sset.has(e.target)) incoming[e.target]++;
  });
  const tier = {}; strategies.forEach((s) => { tier[s.id] = (incoming[s.id] === 0 || improves[s.id] >= 2) ? "primary" : "secondary"; });
  return { nodes, edges, byId, problems, strategies, tier };
}

function SystemMapFigure({ sys }) {
  const { nodes, edges, byId, problems, strategies, tier } = sys;
  const label = (id) => byId[id]?.label || id;
  const pos = {};
  problems.forEach((p, i) => { const a = -Math.PI / 2 + (2 * Math.PI * i) / Math.max(1, problems.length); pos[p.id] = { x: 50 + 20 * Math.cos(a), y: 50 + 20 * Math.sin(a) }; });
  strategies.forEach((s, i) => { const a = -Math.PI / 2 + (2 * Math.PI * i) / Math.max(1, strategies.length); const R = tier[s.id] === "primary" ? 40 : 46; pos[s.id] = { x: 50 + R * Math.cos(a), y: 50 + R * Math.sin(a) }; });
  const catOf = (e) => { if (e.group === "changes_link") return "change"; const rs = roleOfN(byId[e.source] || {}), rt = roleOfN(byId[e.target] || {}); if (rs === "strategy" && rt === "problem") return "improve"; if (rs === "strategy" && rt === "strategy") return "enable"; if (rs === "problem" && rt === "problem") return "worse"; return "other"; };
  return (
    <div className="relative mx-auto aspect-square w-full max-w-[500px]">
      <svg viewBox="0 0 100 100" className="absolute inset-0 h-full w-full" style={{ overflow: "visible" }}>
        <circle cx="50" cy="50" r="27" fill="#0f172a" fillOpacity="0.04" stroke="#cbd5e1" strokeWidth="0.2" strokeDasharray="0.8 0.8" />
        {edges.map((e) => {
          const s = pos[e.source]; if (!s) return null;
          let t; if (e.group === "changes_link") { const a = pos[e.target], b = pos[e.mid_to]; if (!a || !b) return null; t = { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 }; } else { t = pos[e.target]; }
          if (!t) return null;
          const c = catOf(e); const dash = c === "enable" ? "1.5 1" : c === "change" ? "0.8 0.8" : e.loop === "reinforcing" ? undefined : c === "worse" ? "0.5 1" : undefined;
          return <line key={e.id} x1={s.x} y1={s.y} x2={t.x} y2={t.y} stroke={EC[c]} strokeWidth={c === "worse" && e.loop === "reinforcing" ? 0.7 : 0.5} strokeDasharray={dash} opacity="0.75" />;
        })}
      </svg>
      {problems.map((p) => { const q = pos[p.id]; return (
        <div key={p.id} className="absolute w-[96px] -translate-x-1/2 -translate-y-1/2" style={{ left: `${q.x}%`, top: `${q.y}%` }}>
          <div className="px-1.5 py-1 text-center" style={{ background: "#0f172a", color: "#fff" }}><div className="text-[8px] font-semibold leading-tight">{label(p.id)}</div></div>
        </div>
      ); })}
      {strategies.map((s) => { const q = pos[s.id]; const prim = tier[s.id] === "primary"; return (
        <div key={s.id} className="absolute w-[104px] -translate-x-1/2 -translate-y-1/2" style={{ left: `${q.x}%`, top: `${q.y}%` }}>
          <div className="px-1.5 py-1 text-center" style={prim ? { background: "#2563eb", color: "#fff" } : { background: "#fff", color: "#1d4ed8", border: "1px dashed #2563eb" }}>
            <div className="font-mono text-[6.5px] uppercase tracking-wide" style={{ opacity: 0.8 }}>{prim ? "Primary" : "Then"}</div>
            <div className="text-[8px] font-semibold leading-tight">{label(s.id)}</div>
          </div>
        </div>
      ); })}
    </div>
  );
}


export default function PrintReport() {
  const { id } = useParams();
  const [d, setD] = useState(null);
  const reportRef = useRef(null);
  const [emailOpen, setEmailOpen] = useState(false);
  const [recipient, setRecipient] = useState("");
  const [sending, setSending] = useState(false);

  useEffect(() => { api.getSession(id).then(setD).catch(() => {}); }, [id]);

  const genPdfBase64 = async () => {
    const el = reportRef.current;
    const canvas = await html2canvas(el, { scale: 2, backgroundColor: "#ffffff", useCORS: true, logging: false });
    const img = canvas.toDataURL("image/jpeg", 0.92);
    const pdf = new jsPDF("p", "mm", "a4");
    const pw = pdf.internal.pageSize.getWidth();
    const ph = pdf.internal.pageSize.getHeight();
    const imgH = (canvas.height * pw) / canvas.width;
    let heightLeft = imgH;
    let position = 0;
    pdf.addImage(img, "JPEG", 0, position, pw, imgH);
    heightLeft -= ph;
    while (heightLeft > 0) {
      position -= ph;
      pdf.addPage();
      pdf.addImage(img, "JPEG", 0, position, pw, imgH);
      heightLeft -= ph;
    }
    return pdf.output("datauristring").split(",")[1];
  };

  const sendEmail = async () => {
    if (!recipient) return;
    setSending(true);
    try {
      const pdf_base64 = await genPdfBase64();
      await api.emailReport(id, {
        recipient_email: recipient,
        pdf_base64,
        summary: d?.systems_map?.story?.current_dynamic || d?.systems_map?.overview || d?.solutions_of_need?.interpretation || d?.areas_of_need?.interpretation,
      });
      toast.success("Report sent", { description: `Emailed to ${recipient} with the PDF attached.` });
      setEmailOpen(false);
      setRecipient("");
    } catch (e) {
      toast.error("Could not send the report", { description: e?.response?.data?.detail || e.message });
    } finally {
      setSending(false);
    }
  };

  if (!d) return <div className="grid min-h-screen place-items-center"><CircleNotch size={26} className="animate-spin" /></div>;

  const map = d.representation?.map || {};
  const areas = d.areas_of_need?.areas || [];
  const tensions = d.areas_of_need?.area_tensions || [];
  const solutions = d.solutions_of_need?.solutions || [];
  const plan = d.action_plan;
  const catColor = {}; areas.forEach((a, i) => { catColor[a.id] = CAT_COLORS[i % CAT_COLORS.length]; });
  const sysColor = {}; solutions.forEach((s, i) => { sysColor[s.id] = SYS_COLORS[i % SYS_COLORS.length]; });

  // --- new systemic model ---
  const smap = d.systems_map || {};
  const hasSystem = (smap.nodes || []).some((n) => roleOfN(n) !== "sub");
  const sys = hasSystem ? deriveSystem(smap) : null;
  const recSystem = (d.solution_systems?.solution_systems || [])[smap.system_index ?? 0];
  const story = (smap.story && smap.story.current_dynamic) ? smap.story : {
    current_dynamic: d.causal_model?.reinforcing_loops?.[0]?.explanation || d.causal_model?.overview || smap.overview || "",
    intervention: recSystem?.theory_of_change || recSystem?.systemic_aim || "",
    expected_change: recSystem?.beneficial_loops_created?.[0] || recSystem?.why_this_is_systemic || "",
  };
  const eLabel = (id) => sys?.byId[id]?.label || id;
  const improvesOf = (sid) => sys ? sys.edges.filter((e) => e.source === sid && roleOfN(sys.byId[e.target] || {}) === "problem" && e.group !== "changes_link").map((e) => ({ label: eLabel(e.target), why: e.label })) : [];
  const changesOf = (sid) => sys ? sys.edges.filter((e) => e.source === sid && e.group === "changes_link").map((e) => ({ from: eLabel(e.target), to: eLabel(e.mid_to), effect: e.relation, why: e.label })) : [];
  const enablesOf = (sid) => sys ? sys.edges.filter((e) => e.source === sid && sys.byId[e.target] && roleOfN(sys.byId[e.target]) === "strategy").map((e) => eLabel(e.target)) : [];
  const problemLinks = sys ? sys.edges.filter((e) => roleOfN(sys.byId[e.source] || {}) === "problem" && roleOfN(sys.byId[e.target] || {}) === "problem") : [];
  const orderedStrategies = sys ? [...sys.strategies].sort((a, b) => (sys.tier[a.id] === "primary" ? 0 : 1) - (sys.tier[b.id] === "primary" ? 0 : 1)) : [];

  return (
    <div className="min-h-screen bg-white text-zinc-900" style={{ WebkitPrintColorAdjust: "exact", printColorAdjust: "exact" }}>
      <style>{`@media print { .no-print { display: none !important; } @page { margin: 16mm; } .page-break { page-break-before: always; } }`}</style>

      <div className="no-print sticky top-0 z-10 flex items-center justify-between border-b border-zinc-200 bg-white px-6 py-3">
        <span className="font-serif text-lg">Executive Strategy Room — Report</span>
        <div className="flex items-center gap-2">
          <button data-testid="email-report-btn" onClick={() => setEmailOpen(true)} className="inline-flex items-center gap-2 rounded-sm border border-zinc-300 px-4 py-2 text-xs font-medium text-zinc-800 hover:bg-zinc-100"><PaperPlaneTilt size={14} weight="bold" /> Email report</button>
          <button data-testid="print-now-btn" onClick={() => window.print()} className="inline-flex items-center gap-2 rounded-sm bg-zinc-900 px-4 py-2 text-xs font-medium text-white hover:bg-zinc-700"><Printer size={14} weight="bold" /> Print / Save PDF</button>
        </div>
      </div>

      <Dialog open={emailOpen} onOpenChange={setEmailOpen}>
        <DialogContent data-testid="email-dialog" className="max-w-md">
          <DialogHeader><DialogTitle className="font-serif text-2xl">Email this report</DialogTitle></DialogHeader>
          <p className="text-sm text-zinc-600">We'll generate a PDF of this report (both diagrams, analysis and plan) and send it as an attachment.</p>
          <label className="mt-2 block text-xs font-medium text-zinc-500">Recipient email</label>
          <input
            data-testid="email-recipient-input"
            type="email"
            value={recipient}
            onChange={(e) => setRecipient(e.target.value)}
            placeholder="colleague@company.com"
            className="w-full rounded-sm border border-zinc-300 bg-white px-3 py-2.5 text-sm outline-none focus:border-zinc-900"
          />
          <button
            data-testid="email-send-btn"
            onClick={sendEmail}
            disabled={sending || !recipient}
            className="mt-3 inline-flex w-full items-center justify-center gap-2 rounded-sm bg-zinc-900 px-4 py-2.5 text-sm font-medium text-white hover:bg-zinc-700 disabled:opacity-50"
          >
            {sending ? <CircleNotch size={15} className="animate-spin" /> : <PaperPlaneTilt size={15} weight="bold" />}
            {sending ? "Generating & sending…" : "Send report"}
          </button>
        </DialogContent>
      </Dialog>

      <div ref={reportRef} className="mx-auto max-w-[820px] px-8 py-8">
        <div className="mb-1 font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-500">Executive Strategy Room</div>
        <h1 className="font-serif text-3xl leading-tight">{map.current_challenge_formulation || d.session?.title}</h1>
        {map.current_developmental_aim && <p className="mt-2 text-sm leading-relaxed text-zinc-600"><strong>Aim:</strong> {map.current_developmental_aim}</p>}

        {/* Figure 1 */}
        {areas.length > 0 && (
          <section className="mt-8 border-t border-zinc-200 pt-6">
            <h2 className="font-serif text-2xl">Analysis of the Problem</h2>
            <p className="mt-2 text-[15px] leading-relaxed">This challenge isn't one problem — it's a system of {areas.length} interlocking problem categories ({areas.reduce((s, a) => s + a.needs.length, 0)} problems in all). The lines mark the tensions that make solving any one in isolation backfire.</p>
            {d.areas_of_need?.interpretation && <p className="mt-2 text-[15px] leading-relaxed">{d.areas_of_need.interpretation}</p>}
            <div className="mt-5"><div className="mb-1 text-center font-mono text-[10px] uppercase tracking-wide text-zinc-500">Figure 1 · The Problem Space</div><ProblemFigure areas={areas} tensions={tensions} catColor={catColor} /></div>
            <div className="mt-5 space-y-3">
              {areas.map((a) => (
                <div key={a.id} className="border-l-2 pl-3" style={{ borderColor: catColor[a.id] }}>
                  <div className="text-sm font-semibold">{cap(a.name)}</div>
                  <ul className="mt-0.5 list-disc pl-4 text-[13px] leading-relaxed text-zinc-700">{a.needs.map((n) => <li key={n.id}>{n.text}</li>)}</ul>
                </div>
              ))}
            </div>
            {tensions.length > 0 && (
              <div className="mt-4"><div className="font-mono text-[10px] uppercase tracking-wide text-zinc-500">Tensions</div><ul className="mt-1 list-disc pl-4 text-[13px] leading-relaxed text-zinc-700">{tensions.map((t) => <li key={t.id}><strong>{cap(areas.find((a) => a.id === t.area_a)?.name)} ↔ {cap(areas.find((a) => a.id === t.area_b)?.name)}:</strong> {t.statement}</li>)}</ul></div>
            )}
          </section>
        )}

        {/* Figure 2 — the systemic architecture */}
        {sys ? (
          <section className="page-break mt-8 border-t border-zinc-200 pt-6">
            <h2 className="font-serif text-2xl">The System &amp; Its Coordinated Interventions</h2>
            <p className="mt-2 text-[15px] leading-relaxed">This is the system you have — {sys.problems.length} problems that reinforce one another (inner) — and the coordinated architecture of {sys.strategies.length} interventions that acts on it (outer). Some interventions are primary leverage points; others become effective once the system has changed.</p>

            {(story.current_dynamic || story.intervention || story.expected_change) && (
              <div className="mt-4 grid grid-cols-1 gap-2 sm:grid-cols-3">
                {[["Current dynamic", story.current_dynamic, "#dc2626"], ["Strategic intervention", story.intervention, "#2563eb"], ["Expected system change", story.expected_change, "#16a34a"]].map(([t, v, c], i) => (
                  <div key={i} className="border border-zinc-200 bg-zinc-50 px-3 py-2" style={{ borderLeft: `3px solid ${c}` }}>
                    <div className="font-mono text-[9px] uppercase tracking-wide text-zinc-500">{i + 1} · {t}</div>
                    <p className="mt-1 text-[12px] leading-relaxed text-zinc-800">{v || "—"}</p>
                  </div>
                ))}
              </div>
            )}

            <div className="mt-5"><div className="mb-1 text-center font-mono text-[10px] uppercase tracking-wide text-zinc-500">Figure 2 · The System &amp; Intervention Architecture</div><SystemMapFigure sys={sys} /></div>

            <div className="mt-6">
              <div className="font-mono text-[10px] uppercase tracking-wide text-zinc-500">The problem system</div>
              <div className="mt-1.5 space-y-2">
                {sys.problems.map((p) => (
                  <div key={p.id} className="border-l-2 border-zinc-800 pl-3">
                    <div className="text-sm font-semibold">{p.label}</div>
                    {p.summary && <p className="text-[13px] leading-relaxed text-zinc-700">{p.summary}</p>}
                  </div>
                ))}
              </div>
              {problemLinks.length > 0 && (
                <div className="mt-3"><div className="font-mono text-[10px] uppercase tracking-wide text-zinc-500">How the problems reinforce one another</div>
                  <ul className="mt-1 list-disc pl-4 text-[13px] leading-relaxed text-zinc-700">{problemLinks.map((e, i) => <li key={i}>{eLabel(e.source)} <span style={{ color: EC[e.relation] || "#dc2626" }}>{e.label || (e.relation === "better" ? "improves" : e.relation === "enables" ? "enables" : "makes worse")}</span> {eLabel(e.target)}{e.loop ? <span className="text-zinc-500"> · {e.loop}</span> : ""}</li>)}</ul>
                </div>
              )}
            </div>

            <div className="mt-6">
              <div className="font-mono text-[10px] uppercase tracking-wide text-zinc-500">The intervention architecture</div>
              <div className="mt-1.5 space-y-4">
                {orderedStrategies.map((s) => {
                  const prim = sys.tier[s.id] === "primary"; const imp = improvesOf(s.id); const ch = changesOf(s.id); const en = enablesOf(s.id);
                  return (
                    <div key={s.id} className="border-l-2 pl-3" style={{ borderColor: prim ? "#2563eb" : "#93b4fb" }}>
                      <div className="text-sm font-semibold"><span className="font-mono text-[10px]" style={{ color: "#2563eb" }}>{prim ? "PRIMARY" : "SECONDARY"} · </span>{s.label}</div>
                      {s.summary && <p className="mt-0.5 text-[13px] leading-relaxed text-zinc-700">{s.summary}</p>}
                      {imp.length > 0 && <p className="mt-1 text-[12.5px] leading-relaxed text-zinc-700"><strong>Improves:</strong> {imp.map((x) => x.label + (x.why ? ` (${x.why})` : "")).join("; ")}</p>}
                      {ch.length > 0 && <p className="mt-1 text-[12.5px] leading-relaxed" style={{ color: "#6b21a8" }}><strong>Changes the structure:</strong> {ch.map((x) => `${x.effect === "strengthens" ? "strengthens" : "weakens"} ${x.from} → ${x.to}`).join("; ")}</p>}
                      {en.length > 0 && <p className="mt-1 text-[12.5px] leading-relaxed text-zinc-700"><strong>Then enables:</strong> {en.join("; ")}</p>}
                    </div>
                  );
                })}
              </div>
            </div>
          </section>
        ) : solutions.length > 0 ? (
          <section className="page-break mt-8 border-t border-zinc-200 pt-6">
            <h2 className="font-serif text-2xl">Solution Systems — The Way Forward</h2>
            {d.solutions_of_need?.interpretation && <p className="mt-2 text-[15px] leading-relaxed">{d.solutions_of_need.interpretation}</p>}
            <div className="mt-5"><div className="mb-1 text-center font-mono text-[10px] uppercase tracking-wide text-zinc-500">Figure 2 · The Solution Space</div><SolutionsFigure areas={areas} solutions={solutions} catColor={catColor} sysColor={sysColor} /></div>
            <div className="mt-5 space-y-4">
              {solutions.map((s, i) => (
                <div key={s.id} className="border-l-2 pl-3" style={{ borderColor: sysColor[s.id] }}>
                  <div className="text-sm font-semibold"><span className="font-mono text-[10px]" style={{ color: sysColor[s.id] }}>SYSTEM {String.fromCharCode(65 + i)} · </span>{s.title}</div>
                  {s.summary && <p className="mt-0.5 text-[13px] leading-relaxed text-zinc-700">{s.summary}</p>}
                  {s.key_moves?.length > 0 && <ul className="mt-1 list-disc pl-4 text-[13px] leading-relaxed text-zinc-700">{s.key_moves.map((m, j) => <li key={j}>{m}</li>)}</ul>}
                </div>
              ))}
            </div>
          </section>
        ) : null}

        {/* Plan */}
        {plan?.phases?.length > 0 && (
          <section className="page-break mt-8 border-t border-zinc-200 pt-6">
            <h2 className="font-serif text-2xl">Action Plan</h2>
            {plan.headline && <p className="mt-2 text-[15px] leading-relaxed">{plan.headline}</p>}
            <ol className="mt-4 space-y-4">
              {plan.phases.map((ph, i) => (
                <li key={i} className="border-l-2 border-zinc-400 pl-3">
                  <div className="text-sm font-semibold">{String(i + 1).padStart(2, "0")} · {ph.title} {ph.timeframe && <span className="font-normal text-zinc-500">({ph.timeframe})</span>}</div>
                  <ul className="mt-1 list-disc pl-4 text-[13px] leading-relaxed text-zinc-700">{(ph.steps || []).map((st, j) => <li key={j}>{st}</li>)}</ul>
                </li>
              ))}
            </ol>
            {plan.coordination_notes?.length > 0 && <div className="mt-4"><div className="font-mono text-[10px] uppercase tracking-wide text-zinc-500">Coordination points</div><ul className="mt-1 list-disc pl-4 text-[13px] leading-relaxed text-zinc-700">{plan.coordination_notes.map((c, i) => <li key={i}>{c}</li>)}</ul></div>}
          </section>
        )}

        <div className="mt-10 border-t border-zinc-200 pt-3 text-center font-mono text-[10px] uppercase tracking-wide text-zinc-400">Executive Strategy Room</div>
      </div>
    </div>
  );
}
